import { ComponentProps, ReactElement } from 'react'
import { GetStaticPathsContext, GetStaticPropsContext, InferGetStaticPropsType } from 'next'

import Meta from '@/components/base/meta'
import Search from '@/components/layout/advertisements/search'
import PageBuilder from '@/components/layout/builder'
import LandingLayout from '@/components/layout/landing-layout'
import Layout from '@/components/layout/layout'
import Contacts from '@/components/pages/contacts'
import ForgotPassword from '@/components/pages/forgot-password'
import Login from '@/components/pages/login'
import Register from '@/components/pages/register'
import ResetPassword from '@/components/pages/reset-password'
import StudentPosts from '@/components/pages/student-posts'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { WPPageData, WPRoutes } from '@/types'

type LoginPageProps = ComponentProps<typeof Login>['pageProps']
type ContactsPageProps = ComponentProps<typeof Contacts>['pageProps']
type ForgotPasswordPageProps = ComponentProps<typeof ForgotPassword>['pageProps']
type ResetPasswordPageProps = ComponentProps<typeof ResetPassword>['pageProps']
type RegisterPageProps = ComponentProps<typeof Register>['pageProps']
type SearchPageProps = ComponentProps<typeof Search>['pageProps']
type StudentPostsPageProps = ComponentProps<typeof StudentPosts>['pageProps']

export default function Page(pageProps: InferGetStaticPropsType<typeof getStaticProps>) {
  if (pageProps.template === 'template/login.php') {
    return <Login pageProps={pageProps as unknown as LoginPageProps} />
  }

  if (pageProps.template === 'template/contacts.php') {
    return <Contacts pageProps={pageProps as unknown as ContactsPageProps} />
  }

  if (pageProps.template === 'template/forgot-password.php') {
    return <ForgotPassword pageProps={pageProps as unknown as ForgotPasswordPageProps} />
  }

  if (pageProps.template === 'template/reset-password.php') {
    return <ResetPassword pageProps={pageProps as unknown as ResetPasswordPageProps} />
  }

  if (
    pageProps.template === 'template/registrazione-proprietari.php' ||
    pageProps.template === 'template/registrazione-studenti.php'
  ) {
    return <Register pageProps={pageProps as unknown as RegisterPageProps} />
  }

  if (pageProps.template === 'template/ricerca-annunci.php') {
    return <Search pageProps={pageProps as unknown as SearchPageProps} />
  }

  if (pageProps.template === 'template/studenti-annunci-cerco.php') {
    return <StudentPosts postType={1} pageProps={pageProps as unknown as StudentPostsPageProps} />
  }

  if (pageProps.template === 'template/studenti-annunci-offro.php') {
    return <StudentPosts postType={0} pageProps={pageProps as unknown as StudentPostsPageProps} />
  }

  return <PageBuilder pageProps={pageProps} />
}

Page.getLayout = function getLayout(page: ReactElement, pageProps: WPPageData) {
  if (pageProps.template === 'template/landing-page.php') {
    return (
      <LandingLayout pageProps={pageProps}>
        <Meta pageProps={pageProps} />
        {page}
      </LandingLayout>
    )
  }

  return (
    <Layout pageProps={pageProps}>
      <Meta pageProps={pageProps} />
      {page}
    </Layout>
  )
}

export async function getStaticProps({
  params,
  locale,
  defaultLocale
}: GetStaticPropsContext<{ routes: string[] }>) {
  let pageProps: WPPageData | null = null

  const res = await getPromiseFromFetch<WPPageData>({
    method: 'get',
    url: `/${locale === defaultLocale ? '' : `${locale}/`}${params?.routes?.join('/') || ''}/`
  })

  if (res.status === 404) return { notFound: true }

  pageProps = res.data

  return {
    props: {
      ...pageProps
    },
    revalidate: 300
  }
}

export async function getStaticPaths({ defaultLocale, locales }: GetStaticPathsContext) {
  const routes: WPRoutes = (
    await getPromiseFromFetch<WPRoutes>({
      method: 'get',
      url: API.ROUTES
    })
  ).data

  return {
    paths: routes
      .filter((route) => {
        if (!route?.locale) return true
        if (!locales || locales.length === 0) return true

        return locales.includes(route.locale)
      })
      .map((route) => {
        const path = {
          params: {
            routes: route.routes.filter((item) => item !== route.locale)
          }
        } as { params: { routes: string[] }; locale?: string }

        if (route.locale && route.locale !== defaultLocale) {
          path.locale = route.locale
        }

        return path
      }),
    fallback: 'blocking' // false
  }
}
